import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Target, Users, BookOpen, GraduationCap, User, Heart, Compass, ArrowRight, ArrowLeft } from 'lucide-react';
import GrainTexture from '@/components/GrainTexture';
import OptimizedImage from '@/components/OptimizedImage';
import { cn } from '@/lib/utils';
import { SEO } from '@/components/SEO';
import aanbodVisual from '@/assets/webp bestanden/103 - TwentyTwelvePhotography_Systemique.webp';

// SpotlightCard Component
const SpotlightCard = ({ children, className = "", ...props }: any) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(0);
  const [gradientSize, setGradientSize] = useState(600);

  React.useEffect(() => {
    const updateGradientSize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setGradientSize(300);
      } else if (width < 1024) {
        setGradientSize(450);
      } else {
        setGradientSize(600);
      }
    };

    updateGradientSize();
    window.addEventListener('resize', updateGradientSize);
    return () => window.removeEventListener('resize', updateGradientSize);
  }, []);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!divRef.current) return;
    const rect = divRef.current.getBoundingClientRect();
    setPosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const handleFocus = () => { setOpacity(1); };
  const handleBlur = () => { setOpacity(0); };
  const handleMouseEnter = () => { setOpacity(1); };
  const handleMouseLeave = () => { setOpacity(0); };

  return (
    <div
      ref={divRef}
      onMouseMove={handleMouseMove}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-white shadow-xl transition-all duration-300 hover:shadow-2xl border border-transparent hover:border-[#854C73]/20 h-full",
        className
      )}
      {...props}
    >
      <div
        className="pointer-events-none absolute -inset-px transition duration-300 z-0"
        style={{
          opacity,
          background: `radial-gradient(${gradientSize}px circle at ${position.x}px ${position.y}px, rgba(133, 76, 115, 0.15), transparent 40%)`,
        }}
      />
      <div className="relative z-10 h-full flex flex-col">{children}</div>
    </div>
  );
};

function Aanbod() {
  const services = [
    {
      title: "Organisatieopstellingen",
      desc: "Maak organisatiedynamieken zichtbaar en creëer ruimte.",
      icon: Target,
      link: "/organisatieopstellingen",
      anchorText: "Organisatieopstellingen"
    },
    {
      title: "Teamcoaching",
      desc: "Versterk samenwerking door patronen te doorbreken.",
      icon: Users,
      link: "/teamcoaching",
      anchorText: "Teamcoaching"
    },
    {
      title: "Leergang systemisch leiderschap",
      desc: "3-daagse leergang Duiken in de Onderstroom voor leiders en adviseurs.",
      icon: GraduationCap,
      link: "/leergang-systemisch-leiderschap",
      anchorText: "Leergang systemisch leiderschap"
    },
    {
      title: "Opleidingstrajecten",
      desc: "Maatwerk opleidingen en systemische trainingen.",
      icon: BookOpen,
      link: "/opleidingstrajecten",
      anchorText: "Opleidingstrajecten"
    },
    {
      title: "1-op-1 Coaching",
      desc: "Persoonlijke begeleiding met directe diepgang.",
      icon: User,
      link: "/1-op-1",
      anchorText: "1-op-1 Coaching"
    },
    {
      title: "Familieopstellingen",
      desc: "Inzicht in patronen uit je gezin van herkomst.",
      icon: Heart,
      link: "/familieopstellingen",
      anchorText: "Familieopstellingen"
    },
    {
      title: "Sluitertijd",
      desc: "Ontwikkelprogramma voor professionals",
      icon: Compass,
      link: "https://sluitertijd.com",
      anchorText: "Sluitertijd ontwikkelprogramma"
    }
  ];

  return (
    <>
      <SEO 
        title="Aanbod Systemique | Familieopstellingen, Organisatieopstellingen & Teamcoaching"
        description="Ontdek het aanbod van Systemique: familieopstellingen, organisatieopstellingen en systemische teamcoaching in Castricum, Amsterdam, Uitgeest en Noord-Holland."
        canonical="https://systemique.nl/aanbod"
        ogImage="https://systemique.nl/aanbod.png"
        ogTitle="Het Aanbod - Systemique"
        ogDescription="Systemische begeleiding op verschillende niveaus: organisatieontwikkeling, opstellingen, coaching, training en opleidingen."
      />
      
    <div className="min-h-screen bg-[#FAEBEE]">
        {/* Custom Luxury Header */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-6 overflow-hidden bg-[#854C73]">
          <div className="absolute inset-0 z-0">
            <GrainTexture opacity={0.2} />
            <div className="absolute top-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] lg:w-[600px] lg:h-[600px] border border-[#F9EBEF]/10 rounded-full translate-x-1/2 -translate-y-1/2 opacity-30"></div>
          </div>
          
          <div className="container mx-auto relative z-10 text-center">
            <Button variant="ghost" asChild className="mb-8 hover:bg-white/20 text-white hover:text-white border border-white/30 mx-auto flex w-fit px-6 py-2 h-auto cursor-pointer">
              <Link to="/">
                <ArrowLeft className="mr-2 w-4 h-4" /> Terug naar home
              </Link>
            </Button>
            
            <h1 className="font-serif text-3xl md:text-5xl text-[#F9EBEF] mb-6">
              Het <span className="italic font-light font-serif text-[#F9EBEF]">Aanbod</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-[#F9EBEF]/80 font-serif italic max-w-2xl mx-auto">
              Systemische begeleiding op verschillende niveaus.
            </p>
          </div>
        </section>

        <section className="px-4 sm:px-6 pt-10 sm:pt-14">
          <div className="max-w-2xl mx-auto">
            <div className="group rounded-2xl overflow-hidden shadow-md">
              <div className="aspect-[21/9] overflow-hidden">
                <OptimizedImage
                  src={aanbodVisual}
                  webpSrc={aanbodVisual}
                  alt="Systemische opstelling met houten representanten bij Systemique"
                  className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                  width={1200}
                  height={515}
                />
              </div>
            </div>
          </div>
        </section>
        
        {/* Services Grid with Spotlight Cards */}
        <section className="py-12 md:py-24 px-6 relative">
          <GrainTexture opacity={0.1} />
          <div className="container mx-auto max-w-7xl relative z-10">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                const isExternal = service.link.startsWith('http');
                const CardContent = (
                  <SpotlightCard className="p-10">
                    <div className="mb-8 transform group-hover:scale-110 transition-transform duration-500 origin-left">
                      <Icon size={48} strokeWidth={1} className="text-[#854C73]" />
                    </div>
                    
                    <h3 className="font-serif text-2xl md:text-3xl text-[#854C73] mb-4 group-hover:text-[#590D0D] transition-colors leading-tight break-words">
                      {service.title}
                    </h3>
                    
                    <p className="text-[#6b4912] text-lg leading-relaxed mb-10 flex-grow font-light">
                      {service.desc}
                    </p>
                    
                    <div className="flex items-center text-[#854C73] font-bold text-xs tracking-[0.2em] uppercase mt-auto">
                      <span className="border-b-2 border-[#854C73]/20 group-hover:border-[#854C73] transition-all duration-300 pb-1">
                        {service.anchorText}
                      </span> 
                      <ArrowRight size={16} className="ml-2 transform group-hover:translate-x-2 transition-transform duration-300" />
                    </div>
                  </SpotlightCard>
                );
                
                return isExternal ? (
                  <a key={index} href={service.link} target="_blank" rel="noopener noreferrer" className="group block h-full">
                    {CardContent}
                  </a>
                ) : (
                  <Link key={index} to={service.link} className="group block h-full">
                    {CardContent}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Aanbod;